/**
 * VerticalSlotting.js
 * The "Anti-Mud Governor" logic engine.
 * Responsibilities:
 * 1. Detect frequency crowding in critical bands (Mid Range).
 * 2. Resolve collisions via "Shift > Duck > Mute" waterfall.
 */

// Define Buckets
const MID_LOW = 48; // C3
const MID_HIGH = 72; // C5
const MAX_MID_VOICES = 4; // Hard limit

/**
 * Resolves Mix Priority from Instrument Allocation.
 * Returns 1-4 (1 = Imperative, 4 = Expendable). Default 4.
 * Fix: "Priority Shield" - Boosts Counter-Melody/Melody priority in Climax to prevent eviction.
 */
const resolveMixPriority = (role, allocationMap, context) => {
    // 1. Dynamic Shield (Climax)
    // If section is Climax, elevate "Feature" roles (Melody, Counter) above Background (Harmony Rhythmic)
    if (context && context.section && (context.section.includes('CLIMAX') || context.section.includes('DROP'))) {
        if (role === 'melody') return 0; // Absolute Priority
        if (role === 'counter_melody') return 1; // High Priority
        if (role === 'harmony_rhythmic') return 4; // Expendable (Aggressor)
    }

    const assignment = allocationMap[role];
    if (assignment && assignment.length > 0 && assignment[0].instrument) {
        return assignment[0].instrument.mix_priority || 3;
    }
    // Fallback based on Role
    if (role === 'melody') return 1;
    if (role === 'bass') return 2;
    if (role.includes('harmony')) return 3;
    return 4;
};

/**
 * Checks if a pitch is within the instrument's valid range and tessitura.
 * @param {number} pitch - MIDI pitch
 * @param {Object} instrument - Instrument definition
 * @returns {boolean}
 */
const isPitchValid = (pitch, instrument) => {
    if (!instrument) return true; // Optimistic fallback

    // 1. Check Physical Range (Hard Limit)
    if (instrument.range) {
        if (pitch < instrument.range.min || pitch > instrument.range.max) return false;
    }

    // 2. Check Tessitura (Soft Guide - treated as "Safe Zone" for automated shifting)
    // If we shift OUT of tessitura, we might accept it if it's still in range,
    // but ideally we stay in tessitura if defined.
    // For "Octave Escape", Range is the primary constraint.
    return true;
};

/**
 * Main Slotting Logic
 * @param {Object} abstractTracks - Map of role -> event[]
 * @param {Object} context - Generation context
 * @param {Object} allocationMap - Role allocation map
 * @returns {Object} { tracks, stats: { collisionCount, totalChecks } }
 */
export const applyVerticalSlotting = (abstractTracks, context, allocationMap = {}) => {
    // 1. Collect references
    let voiceMap = []; // [ { event, role } ]
    Object.keys(abstractTracks).forEach(role => {
         if (abstractTracks[role]) {
             abstractTracks[role].forEach(ev => {
                 // Only check pitched events
                 if (ev.d !== undefined && !role.includes('perc')) {
                     voiceMap.push({ event: ev, role });
                 }
             });
         }
    });

    if (voiceMap.length === 0) return { tracks: abstractTracks, stats: { collisionCount: 0, totalChecks: 0 } };

    // Calculate time range
    voiceMap.sort((a,b) => a.event.s - b.event.s);
    const start = Math.floor(voiceMap[0].event.s);
    const end = Math.ceil(voiceMap[voiceMap.length-1].event.s + 4);

    let collisionCount = 0;
    let totalChecks = 0;

    // 2. Scan Time Grid (16th note resolution)
    for (let t = start; t < end; t += 0.25) {
         totalChecks++;

         // Find active events at this time step
         const active = voiceMap.filter(v =>
             !v.event.muted &&
             v.event.s <= t + 0.05 &&
             v.event.s + v.event.len > t + 0.05
         );

         // Check Mid Bucket Occupancy
         const midVoices = active.filter(v => v.event.d >= MID_LOW && v.event.d <= MID_HIGH);

         if (midVoices.length > MAX_MID_VOICES) {
             collisionCount++;

             // Sort by Mix Priority (Descending: High number (4) = Low Priority = First Victim)
             midVoices.sort((a,b) => resolveMixPriority(b.role, allocationMap, context) - resolveMixPriority(a.role, allocationMap, context));

             const excess = midVoices.length - MAX_MID_VOICES;

             for(let i=0; i<excess; i++) {
                 const victim = midVoices[i];
                 const instrument = (allocationMap[victim.role] && allocationMap[victim.role][0])
                                    ? allocationMap[victim.role][0].instrument
                                    : null;

                 // --- ATTEMPT 1: The "Octave Escape" (Shift) ---
                 let shiftSuccess = false;

                 // Try shifting UP first (Visibility)
                 const upPitch = victim.event.d + 12;
                 // Avoid moving into High bucket? MID_HIGH is 72. 72+ is fine.
                 // Just ensure valid range.
                 if (isPitchValid(upPitch, instrument)) {
                     victim.event.d = upPitch;
                     victim.event.vel = (victim.event.vel || 0.7) * 0.9; // 10% penalty
                     shiftSuccess = true;
                 } else {
                     // Try shifting DOWN
                     const downPitch = victim.event.d - 12;
                     if (isPitchValid(downPitch, instrument)) {
                         victim.event.d = downPitch;
                         victim.event.vel = (victim.event.vel || 0.7) * 0.9;
                         shiftSuccess = true;
                     }
                 }

                 // Check if shift actually resolved the collision (moved out of [48, 72])
                 if (shiftSuccess) {
                     if (victim.event.d >= MID_LOW && victim.event.d <= MID_HIGH) {
                         shiftSuccess = false; // Still stuck in mud
                     }
                 }

                 if (shiftSuccess) continue; // Saved!

                 // --- ATTEMPT 2: The "Ghost Mode" (Duck) ---
                 // Check dynamic profile
                 let canDuck = true;
                 if (instrument && instrument.dynamic_profile) {
                     if (instrument.dynamic_profile === 'compressed' || instrument.dynamic_profile === 'consistent') {
                         canDuck = false; // These instruments need consistency
                     }
                 }

                 if (canDuck) {
                     victim.event.vel = 0.3; // Background texture
                     // We keep the note, but effectively silence it in the mix calculation?
                     // Technically it's still occupying a frequency slot, but at low energy.
                     // The prompt says "Action: ... Keep it."
                     // Does this resolve the "Traffic Control" need to mute?
                     // "We want to Preserve the note... rather than killing it."
                     // So yes, we mark it handled.
                     continue;
                 }

                 // --- FINAL RESORT: Mute ---
                 victim.event.muted = true;
             }
         }
    }

    // Apply Mutes (Filter out muted events)
    Object.keys(abstractTracks).forEach(role => {
        if (abstractTracks[role]) {
            abstractTracks[role] = abstractTracks[role].filter(ev => !ev.muted);
        }
    });

    return { tracks: abstractTracks, stats: { collisionCount, totalChecks } };
};
